/**
 * 카카오 알림톡 3단계 토글 설정 (2026-07-05)
 * 전체(master) → 카테고리(category) → 개별(type)
 * 발송 = 전체 ON AND 카테고리 ON AND 개별 ON  (백엔드 alimtalk_gate 와 동일 규칙)
 *
 * API:
 *   GET  /api/v1/owner/integration-settings/alimtalk  -> { config, meta }
 *   PUT  /api/v1/owner/integration-settings/alimtalk   (config 저장)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE_URL } from '../../../config/api';

interface AlimtalkConfig {
  master: boolean;
  categories: Record<string, boolean>;
  types: Record<string, boolean>;
  // 이용권 만료 알림 날짜별 스위치 (만료 며칠 전). 없으면 전부 켬으로 본다.
  ticket_expiry_days?: Record<string, boolean>;
  // 화장실 외출 뒤 입장 알림 기준(분). 이 시간 안에 돌아오면 입장 알림을 안 보낸다. 기본 30.
  toilet_return_minutes?: number;
}
interface TemplateVariable {
  name: string;
  desc: string;
  example: string;
}
interface TypeMeta {
  label: string;
  category: string;
  template_code: string;
  recipient: string;
  trigger: string;
  approval: string; // approved | pending | unregistered
  approval_label: string;
  locked?: boolean;         // 카카오 검수 전 — 켜도 발송되지 않는다
  status_checked?: boolean; // 카카오에 직접 물어 확인한 값인지
  status_label?: string;
  body: string;
  variables: TemplateVariable[];
  example: string;
}
interface AlimtalkMeta {
  categories: Record<string, string>; // categoryKey -> label
  types: Record<string, TypeMeta>;
}

const APPROVAL_STYLE: Record<string, string> = {
  approved: 'bg-green-50 text-green-700 border-green-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  unregistered: 'bg-red-50 text-red-700 border-red-200',
};

// 각 알림이 어떤 문구로·언제·누구에게 발송되는지 미리보기
const TemplatePreview: React.FC<{ meta: TypeMeta }> = ({ meta }) => {
  const badge = APPROVAL_STYLE[meta.approval] || APPROVAL_STYLE.unregistered;
  const isExample = meta.approval !== 'approved';
  return (
    <div className="mx-4 mb-2 rounded-md border border-gray-200 bg-gray-50 p-3 text-xs">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 font-medium ${badge}`}>
          {meta.approval_label}
        </span>
        {meta.template_code && <span className="text-gray-400">코드: {meta.template_code}</span>}
      </div>
      <div className="mb-2 grid grid-cols-[auto,1fr] gap-x-2 gap-y-1 text-gray-600">
        <span className="text-gray-400">받는 사람</span>
        <span>{meta.recipient || '-'}</span>
        <span className="text-gray-400">발송 시점</span>
        <span>{meta.trigger || '-'}</span>
      </div>
      <div className="mb-1 text-gray-400">발송 예시</div>
      <div className="whitespace-pre-line rounded-lg border border-gray-200 bg-white p-3 leading-relaxed text-gray-800">
        {meta.example || '(문구 없음)'}
      </div>
      {isExample && (
        <div className="mt-2 text-[11px] text-amber-700">
          {meta.status_checked
            ? '※ 아래는 예시 문구이며, 이 알림은 카카오 검수가 끝나지 않아 아직 발송되지 않습니다.'
            : '※ 예시 문구입니다. 실제 발송 문구·승인 여부는 NHN 등록본 확인이 필요합니다.'}
        </div>
      )}
      {meta.variables?.length > 0 && (
        <div className="mt-2">
          <div className="mb-1 text-gray-400">변수</div>
          <ul className="space-y-0.5">
            {meta.variables.map((v) => (
              <li key={v.name} className="text-gray-600">
                <code className="text-blue-600">{`#{${v.name}}`}</code> — {v.desc}
                <span className="text-gray-400"> (예: {v.example})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// 이용권 만료 알림 날짜별 스위치 — 백엔드 alimtalk_gate.TICKET_EXPIRY_DAYS 와 같아야 한다.
const TICKET_EXPIRY_DAYS: { key: string; label: string }[] = [
  { key: '7', label: '7일 전' },
  { key: '3', label: '3일 전' },
  { key: '1', label: '1일 전' },
];

// 화장실 복귀 입장 알림 기준(분) — 백엔드 alimtalk_gate 의 범위(5~120)와 맞춘다.
const TOILET_RETURN_OPTIONS = [5, 10, 15, 20, 30, 45, 60, 90, 120];
const TOILET_RETURN_DEFAULT = 30;

const getToken = () =>
  localStorage.getItem('auth_access_token') || localStorage.getItem('accessToken');

const Toggle: React.FC<{
  checked: boolean;
  disabled?: boolean;
  onChange: () => void;
}> = ({ checked, disabled, onChange }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      disabled ? 'opacity-40 cursor-not-allowed ' : ''
    }${checked ? 'bg-blue-600' : 'bg-gray-300'}`}
    aria-pressed={checked}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
        checked ? 'translate-x-6' : 'translate-x-1'
      }`}
    />
  </button>
);

const AlimtalkSettings: React.FC = () => {
  const [config, setConfig] = useState<AlimtalkConfig | null>(null);
  const [meta, setMeta] = useState<AlimtalkMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openPreview, setOpenPreview] = useState<Record<string, boolean>>({});
  const togglePreview = (t: string) =>
    setOpenPreview((p) => ({ ...p, [t]: !p[t] }));

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = getToken();
      if (!token) {
        setError('로그인이 필요합니다.');
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/v1/owner/integration-settings/alimtalk`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('알림톡 설정을 불러오지 못했습니다.');
      const data = await res.json();
      setConfig(data.config);
      setMeta(data.meta);
    } catch (e: any) {
      setError(e.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // 낙관적 업데이트 + 저장. 실패 시 롤백.
  const persist = useCallback(async (next: AlimtalkConfig) => {
    const prev = config;
    setConfig(next);
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/api/v1/owner/integration-settings/alimtalk`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(next),
      });
      if (!res.ok) throw new Error('저장에 실패했습니다.');
    } catch (e: any) {
      setConfig(prev); // 롤백
      setError(e.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  }, [config]);

  const toggleMaster = () => {
    if (!config) return;
    persist({ ...config, master: !config.master });
  };
  const toggleCategory = (cat: string) => {
    if (!config) return;
    persist({ ...config, categories: { ...config.categories, [cat]: !config.categories[cat] } });
  };
  const toggleType = (t: string) => {
    if (!config) return;
    persist({ ...config, types: { ...config.types, [t]: !config.types[t] } });
  };

  const isDayOn = (d: string) => config?.ticket_expiry_days?.[d] !== false;
  const toggleTicketDay = (d: string) => {
    if (!config) return;
    const days: Record<string, boolean> = {};
    TICKET_EXPIRY_DAYS.forEach(({ key }) => { days[key] = isDayOn(key); });
    days[d] = !days[d];
    persist({ ...config, ticket_expiry_days: days });
  };

  const toiletMinutes = config?.toilet_return_minutes ?? TOILET_RETURN_DEFAULT;
  const changeToiletMinutes = (m: number) => {
    if (!config) return;
    persist({ ...config, toilet_return_minutes: m });
  };

  if (loading) {
    return <div className="text-sm text-gray-500 py-4">알림톡 설정 불러오는 중…</div>;
  }
  if (!config || !meta) {
    return (
      <div className="text-sm text-red-600 py-4">
        {error || '설정을 불러오지 못했습니다.'}
        <button onClick={load} className="ml-2 underline">다시 시도</button>
      </div>
    );
  }

  const categoryKeys = Object.keys(meta.categories);
  const typesByCategory = (cat: string) =>
    Object.entries(meta.types).filter(([, v]) => v.category === cat);

  return (
    <div className="space-y-4">
      {/* 전체(master) */}
      <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <div>
          <div className="font-semibold text-gray-900">전체 알림톡</div>
          <div className="text-xs text-gray-500">
            끄면 아래 설정과 무관하게 모든 알림톡이 발송되지 않습니다.
          </div>
        </div>
        <Toggle checked={config.master} onChange={toggleMaster} />
      </div>

      {!config.master && (
        <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
          ⚠️ 전체 알림톡이 꺼져 있어 개별 설정과 상관없이 발송되지 않습니다.
        </div>
      )}

      {/* 카테고리 → 개별 */}
      {categoryKeys.map((cat) => {
        const catOn = config.categories[cat] !== false;
        const disabledByParent = !config.master;
        return (
          <div key={cat} className="rounded-lg border border-gray-200">
            {/* 카테고리 헤더 */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-white rounded-t-lg">
              <div className="font-medium text-gray-900">{meta.categories[cat]}</div>
              <Toggle
                checked={catOn}
                disabled={disabledByParent}
                onChange={() => toggleCategory(cat)}
              />
            </div>
            {/* 개별 타입 */}
            <div className="divide-y divide-gray-50">
              {typesByCategory(cat).map(([tkey, tval]) => {
                const on = !!config.types[tkey];
                // 검수 전 서식은 켜도 발송되지 않는다 → 새로 켜는 것만 막는다.
                // 이미 켜 둔 것은 끌 수 있어야 하므로 잠그지 않는다.
                const blocked = !!tval.locked;
                const rejected = tval.approval === 'rejected';
                const typeDisabled = disabledByParent || !catOn || (blocked && !on);
                const opened = !!openPreview[tkey];
                return (
                  <div key={tkey}>
                    <div className="flex items-center justify-between px-4 py-2 pl-8">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="text-sm text-gray-700">{tval.label}</span>
                        {blocked && (
                          <span
                            className={`flex-shrink-0 rounded-full border px-2 py-0.5 text-[11px] ${
                              rejected
                                ? 'border-red-200 bg-red-50 text-red-700'
                                : 'border-amber-200 bg-amber-50 text-amber-700'
                            }`}
                          >
                            {rejected ? '카카오 반려' : '카카오 검수중'}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => togglePreview(tkey)}
                          className="flex-shrink-0 text-xs text-blue-600 hover:underline"
                          aria-expanded={opened}
                        >
                          미리보기 {opened ? '▲' : '▼'}
                        </button>
                      </div>
                      <Toggle
                        checked={on}
                        disabled={typeDisabled}
                        onChange={() => toggleType(tkey)}
                      />
                    </div>
                    {blocked && (
                      <div
                        className={`px-4 pb-2 pl-8 text-[11px] ${
                          rejected ? 'text-red-700' : 'text-amber-700'
                        }`}
                      >
                        {rejected
                          ? '⚠️ 카카오에서 반려된 문구라 이 알림은 발송되지 않습니다. 문구를 고쳐 다시 등록해야 하니 본사에 문의해 주세요.'
                          : on
                          ? '⚠️ 카카오 검수가 끝나지 않아 켜져 있어도 지금은 발송되지 않습니다. 승인되면 자동으로 나갑니다.'
                          : '카카오 검수가 끝나면 자동으로 켤 수 있게 됩니다. 지금 켜도 발송되지 않습니다.'}
                      </div>
                    )}
                    {tkey === 'entry_exit' && (
                      <div className="px-4 pb-2 pl-12">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span
                            className={`text-xs ${typeDisabled || !on ? 'text-gray-400' : 'text-gray-500'}`}
                          >
                            화장실 다녀온 뒤 입장 알림
                          </span>
                          <select
                            value={toiletMinutes}
                            disabled={typeDisabled || !on}
                            onChange={(e) => changeToiletMinutes(Number(e.target.value))}
                            className="rounded border border-gray-300 px-1.5 py-0.5 text-xs disabled:opacity-40"
                          >
                            {TOILET_RETURN_OPTIONS.map((m) => (
                              <option key={m} value={m}>
                                {m}분
                              </option>
                            ))}
                          </select>
                          <span
                            className={`text-xs ${typeDisabled || !on ? 'text-gray-400' : 'text-gray-500'}`}
                          >
                            넘게 걸렸을 때만 보내기
                          </span>
                        </div>
                        <div className="mt-1 text-[11px] text-gray-400">
                          화장실 외출 후 이 시간 안에 돌아오면 학부모님께 입장 알림을 보내지 않습니다.
                          기본 30분입니다.
                        </div>
                      </div>
                    )}
                    {tkey === 'ticket_expiry' && (
                      <div className="px-4 pb-2 pl-12">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                          <span className="text-xs text-gray-500">보낼 날짜</span>
                          {TICKET_EXPIRY_DAYS.map(({ key, label }) => (
                            <label
                              key={key}
                              className={`flex items-center gap-1.5 text-xs ${
                                typeDisabled || !on ? 'text-gray-400' : 'text-gray-700'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isDayOn(key)}
                                disabled={typeDisabled || !on}
                                onChange={() => toggleTicketDay(key)}
                              />
                              만료 {label}
                            </label>
                          ))}
                        </div>
                        {on && TICKET_EXPIRY_DAYS.every(({ key }) => !isDayOn(key)) && (
                          <div className="mt-1 text-[11px] text-amber-700">
                            ⚠️ 날짜를 하나도 고르지 않아 이용권 만료 알림이 발송되지 않습니다.
                          </div>
                        )}
                      </div>
                    )}
                    {opened && <TemplatePreview meta={tval} />}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {error && <div className="text-xs text-red-600">{error}</div>}
      {saving && <div className="text-xs text-gray-400">저장 중…</div>}
    </div>
  );
};

export default AlimtalkSettings;
